import { z } from 'zod'

/**
 * Zod schema for client form validation
 * Conditional validation: company_name required for corporate types,
 * first_name/last_name required for individual type
 */
export const clientFormSchema = z
  .object({
    // Client Type (required)
    client_type: z.enum(['individual', 'corporate', 'government', 'non_profit'], {
      error: 'Client type is required',
    }),

    // Name fields
    first_name: z.string().max(100).optional().or(z.literal('')),
    last_name: z.string().max(100).optional().or(z.literal('')),
    company_name: z.string().max(200).optional().or(z.literal('')),
    legal_entity_name: z.string().max(200).optional().or(z.literal('')),

    // Contact information
    email_primary: z
      .string()
      .email('Invalid email address')
      .optional()
      .or(z.literal('')),
    phone_primary: z.string().max(30).optional().or(z.literal('')),
    phone_secondary: z.string().max(30).optional().or(z.literal('')),

    // Address
    address_line1: z.string().max(200).optional().or(z.literal('')),
    address_line2: z.string().max(200).optional().or(z.literal('')),
    city: z.string().max(100).optional().or(z.literal('')),
    state_province: z.string().max(50).optional().or(z.literal('')),
    postal_code: z.string().max(20).optional().or(z.literal('')),
    country: z.string().max(100).optional().or(z.literal('')),

    // Billing
    payment_terms: z
      .enum(['due_on_receipt', 'net_15', 'net_30', 'net_45', 'net_60', 'custom'])
      .optional(),
    tax_exempt: z.boolean().optional(),
    tax_id: z.string().max(50).optional().or(z.literal('')),

    // Status
    status: z.enum(['lead', 'prospect', 'active', 'inactive', 'suspended']).optional(),

    // Notes
    notes: z.string().max(5000).optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    const isCompanyType =
      data.client_type === 'corporate' ||
      data.client_type === 'government' ||
      data.client_type === 'non_profit'

    if (isCompanyType) {
      if (!data.company_name || data.company_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Company name is required for this client type',
          path: ['company_name'],
        })
      }
    } else {
      // Individual type
      if (!data.first_name || data.first_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'First name is required for individual clients',
          path: ['first_name'],
        })
      }
      if (!data.last_name || data.last_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Last name is required for individual clients',
          path: ['last_name'],
        })
      }
    }
  })

// Input type for form defaultValues
export type ClientFormInput = z.input<typeof clientFormSchema>

// Output type after validation/parsing
export type ClientFormValues = z.output<typeof clientFormSchema>

// Default values for new client form
export const clientFormDefaults: ClientFormInput = {
  client_type: 'individual',
  first_name: '',
  last_name: '',
  company_name: '',
  legal_entity_name: '',
  email_primary: '',
  phone_primary: '',
  phone_secondary: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
  payment_terms: 'net_30',
  tax_exempt: false,
  tax_id: '',
  status: 'active',
  notes: '',
}
